import { createContext, ReactNode, useContext } from 'react';
import { Location, useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { CacheAreaKey } from 'core/config/cache-config';
import { FeatureFlag } from 'core/enums/feature-flag';
import { ActionOverlays } from 'core/enums/overlay/action-overlays';
import { PaymentAttribution } from 'core/enums/payment-type';
import { StorageHelper } from 'core/services/storage';
import { PaymentResponseMessage } from 'core/stores/payment/payment-polling.store';
import { PaymentStatus, PaymentStore } from 'core/stores/payment/payment.store';

export type PaymentMetaData = {
  paymentAttribution: PaymentAttribution;
  journeyName?: ActionOverlays | FeatureFlag;
  eventData: { [key: string]: any };
  analyticsTagging?: {
    context?: string;
    fsp_name?: string;
    componentName: string;
    areaName: string;
    sectionName?: string;
    entryPoint?: string;
  };
};

export type PaymentSession = {
  message: PaymentResponseMessage;
  transactionId: string;
  status: PaymentStatus;
  deReferenceId: string;
  deTransactionRef: string;
  paymentMetaData: PaymentMetaData;
  isRecurringUpdate?: boolean;
};

export type PaymentContextProps = {
  latestStatus: PaymentStatus;
  hasMadePayment: (action: ActionOverlays | FeatureFlag) => boolean;
  handlePaymentProcessing: () => void;
  redirectUrl?: string;
} & PaymentSession;

const PaymentContext = createContext<PaymentContextProps | null>(null);

export const PaymentContextProvider = observer<{ children: ReactNode; paymentStore: PaymentStore }>(
  ({ children, paymentStore }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { pollingStore } = paymentStore;

    const paymentSession =
      (StorageHelper.GetInstance.getFromCache('sessions', 'create', CacheAreaKey.Payments) as PaymentSession) || {};

    const handlePaymentProcessing = () => {
      const entryPaymentStatus = [PaymentStatus.Start, PaymentStatus.Created];

      // NOTE: Check for Pay@ url parameters for CCDO
      const isPayAt = handlePayAtQueryParameters();

      if (isPayAt) {
        // paymentStore.clearState();
        window.location.assign(`${window.location.origin}${window.location.pathname}${getHashParameters().hash}`);
        return;
      }

      if (!paymentSession || !entryPaymentStatus.includes(paymentSession.status)) {
        return;
      }

      const queryParams = new URLSearchParams(location.search);

      // NOTE: Only process payments on return from payment gateway
      if (!queryParams.has('payRes') || (paymentSession.status === PaymentStatus.Pending && pollingStore.isPolling)) {
        return;
      }

      // NOTE: Certain DPP payments potentially stay on Pending status when redirected to the failure screen for extended periods.
      const payRes = queryParams.get('payRes');
      if (payRes === 'failure') {
        pollingStore.paymentFailed('Failed response');
      } else {
        pollingStore.startPoll(paymentSession.transactionId);
      }

      queryParams.delete('payRes');
      location.search = `?${queryParams.toString()}`;

      let newLocation: string | Location = location;

      // NOTE: Navigate to generic success/failure overlay if not already in an action (all actions should complete their own payments)
      if (!location.search.startsWith(`?action=`) && !location.pathname.includes('/customer/create')) {
        newLocation = `/action?action=${ActionOverlays.PaymentResult}`;
        StorageHelper.GetInstance.setCache('actionReturn', location);
      }

      navigate(newLocation);
    };

    const hasMadePayment = (action: ActionOverlays | FeatureFlag) => {
      return (
        !!paymentSession?.status &&
        paymentSession.status !== PaymentStatus.None &&
        paymentSession.status !== PaymentStatus.Start &&
        paymentSession.paymentMetaData &&
        paymentSession.paymentMetaData.journeyName === action
      );
    };

    const handlePayAtQueryParameters = () => {
      const payAtParameters = [
        'digiapiPaymentMethod',
        'digiapiExternalRef',
        'payatTranId',
        'payatReferenceNum',
        'digiapiPaymentStatus',
        'digiapiResponseMsg',
      ];

      const search = window.location.search;
      const searchParams = new URLSearchParams(search);

      const hashQuery = getHashParameters().hashQuery;
      const hashQueryParams = new URLSearchParams(hashQuery);

      const hasResponse = payAtParameters.some((param) => searchParams.get(param) || hashQueryParams.get(param));

      if (!hasResponse) {
        return;
      }

      const queryParams = searchParams.size > 0 ? searchParams : hashQueryParams;

      const status = queryParams.get('digiapiPaymentStatus')?.toLowerCase();
      const message = queryParams.get('digiapiResponseMsg')?.toLowerCase() || 'Unknown Error';
      const isSuccess = status === 'success' || message === 'payment successful';

      if (isSuccess) {
        pollingStore.paymentSuccess();
        return true;
      }

      pollingStore.paymentFailed(message);
      return true;
    };

    const getHashParameters = () => {
      const hash = window.location.hash;
      const hashArr = hash.split('?');

      return {
        hash: hashArr[0],
        hashQuery: hashArr.length > 1 ? hashArr[1] : '',
      };
    };

    return (
      <PaymentContext.Provider
        value={{
          ...paymentSession,
          message: pollingStore?.message || paymentSession?.message,
          latestStatus: pollingStore?.status,
          hasMadePayment: hasMadePayment,
          handlePaymentProcessing: handlePaymentProcessing,
        }}
      >
        {children}
      </PaymentContext.Provider>
    );
  }
);

export const usePaymentContext = () => {
  const context = useContext(PaymentContext);

  if (!context) {
    throw new Error('Missing Payment Context');
  }

  return context;
};

export default PaymentContextProvider;